<template>
    <div>
        <button
            v-if="shipmentBatchStatusCanBeUpdated"
            name="shipment_batch_status"
            :class="[
                bgColor,
                hoverBgColor,
                'inline-flex justify-center items-center flex-wrap w-full px-3 py-3 rounded-md text-sm font-medium text-white focus:outline-none',
            ]"
            @click="openModal"
        >
            <component :is="icon" class="h-6 w-6 mr-2" aria-hidden="true" />
            <span class="text-xl">
                {{ status }}
            </span>
        </button>

        <div
            v-else
            :class="[
                bgColor,
                'inline-flex justify-center items-center flex-wrap w-full px-3 py-3 rounded-md text-white',
            ]"
        >
            <component :is="icon" class="h-6 w-6 mr-2" aria-hidden="true" />
            <span class="text-xl">
                {{ status }}
            </span>
        </div>

        <TransitionRoot
            v-if="shipmentBatchStatusCanBeUpdated"
            as="template"
            :show="open"
        >
            <Dialog
                as="div"
                static
                class="fixed z-20 inset-0 overflow-y-auto"
                @close="open = false"
                :open="open"
            >
                <div
                    class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <DialogOverlay
                            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                        />
                    </TransitionChild>

                    <span
                        class="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                        >
                            <div
                                class="hidden sm:block absolute top-0 right-0 pt-4 pr-4"
                            >
                                <button
                                    type="button"
                                    class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                                    @click="open = false"
                                >
                                    <span class="sr-only">Close</span>
                                    <XIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <TruckIcon
                                        class="h-6 w-6 text-nutreance-green"
                                        aria-hidden="true"
                                    />
                                </div>

                                <div class="mt-3 sm:mt-0 w-full sm:mr-10">
                                    <DialogTitle
                                        as="h3"
                                        class="mt-6 sm:mt-2 text-xl leading-6 font-medium text-gray-800 sm:ml-4 text-center sm:text-left"
                                    >
                                        Update Shipment Batch #{{
                                            shipmentBatch.id
                                        }}
                                        Status
                                    </DialogTitle>
                                </div>
                            </div>

                            <form
                                ref="form"
                                :action="actionUrl"
                                method="POST"
                                @submit.prevent="onSubmit(form)"
                            >
                                <div
                                    class="px-4 py-3 sm:px-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
                                >
                                    <input
                                        type="hidden"
                                        name="_token"
                                        :value="csrf"
                                    />
                                    <input
                                        type="hidden"
                                        name="_method"
                                        value="PATCH"
                                    />

                                    <div class="sm:col-span-2 sm:px-16 pb-6">
                                        <div class="py-2">
                                            <label
                                                for="status"
                                                class="block text-sm font-medium text-gray-700"
                                            >
                                                Status
                                            </label>

                                            <div
                                                class="mt-1 relative rounded-md shadow-sm"
                                            >
                                                <select
                                                    v-model="inputs.status"
                                                    :ref="refs.status"
                                                    id="status"
                                                    name="status"
                                                    required
                                                    :class="[
                                                        hasError(
                                                            errors,
                                                            'status',
                                                        )
                                                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-14'
                                                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                                        'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm',
                                                    ]"
                                                    :aria-invalid="
                                                        hasError(
                                                            errors,
                                                            'status',
                                                        )
                                                            ? 'true'
                                                            : 'false'
                                                    "
                                                    :aria-describedby="
                                                        hasError(
                                                            errors,
                                                            'status',
                                                        )
                                                            ? 'status-error'
                                                            : ''
                                                    "
                                                >
                                                    <option
                                                        v-for="(
                                                            statusOptionDescription,
                                                            statusOptionValue
                                                        ) in statusOptions"
                                                        :key="statusOptionValue"
                                                        :value="
                                                            statusOptionValue
                                                        "
                                                    >
                                                        {{
                                                            statusOptionDescription
                                                        }}
                                                    </option>
                                                </select>
                                                <div
                                                    v-if="
                                                        hasError(
                                                            errors,
                                                            'status',
                                                        )
                                                    "
                                                    class="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none"
                                                >
                                                    <ExclamationCircleIcon
                                                        class="h-5 w-5 text-red-500"
                                                        aria-hidden="true"
                                                    />
                                                </div>
                                            </div>
                                            <p
                                                v-if="
                                                    hasError(errors, 'status')
                                                "
                                                class="mt-2 text-sm text-red-600"
                                                id="status-error"
                                            >
                                                {{ error(errors, "status") }}
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        name="yes"
                                        type="submit"
                                        class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-nutreance-green text-base font-medium text-white hover:bg-nutreance-green-darker focus:outline-none sm:col-start-2 sm:text-sm"
                                    >
                                        <LoadingIcon
                                            v-if="submitted"
                                            class="h-5 w-5 mr-2"
                                        />
                                        <CheckIcon
                                            v-else
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Save
                                    </button>

                                    <button
                                        name="no"
                                        type="button"
                                        class="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-500 hover:bg-red-600 text-base font-medium text-white focus:outline-none sm:mt-0 sm:col-start-1 sm:text-sm"
                                        @click="open = false"
                                    >
                                        <XIcon
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, reactive, ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { ClockIcon } from "@heroicons/vue/outline";
import {
    CheckIcon,
    ExclamationCircleIcon,
    PrinterIcon,
    TruckIcon,
    XIcon,
} from "@heroicons/vue/solid";
import { useForm } from "../../functions/useForm";
import LoadingIcon from "../LoadingIcon.vue";
import { startCase } from "lodash";

const props = defineProps<{
    csrf?: string;
    errors?: any;
    old?: any;
    shipmentBatch?: any;
    shipmentBatchStatusCanBeUpdated?: string;
    statusOptions?: any;
}>();

const { error, hasError, oldInput, onSubmit, submitted } = useForm();
const form = ref<any>(null);
const actionUrl = "/user/shipment-batches/" + props.shipmentBatch.id;
const open = ref(!!Object.prototype.hasOwnProperty.call(props.old, "status"));

const bgColor = ref<string | null>(null);
const hoverBgColor = ref<string | null>(null);
const icon = ref<any>(null);
const status = startCase(
    props.shipmentBatch.status.replace(/_/g, " ").toLowerCase(),
);

const refs = {
    status: ref<any>(null),
};

const inputs = reactive({
    status: oldInput(props.old, "status")
        ? oldInput(props.old, "status")
        : Object.keys(props.statusOptions)[0],
});

switch (props.shipmentBatch.status) {
    case "FULLY_PRINTED":
        bgColor.value = "bg-nutreance-green";
        hoverBgColor.value = "hover:bg-nutreance-green-darker";
        icon.value = PrinterIcon;
        break;

    case "SHIPPING_LABELS_PRINTED":
    case "PACKING_SLIPS_PRINTED":
    case "PRINTING":
        bgColor.value = "bg-amber-500";
        hoverBgColor.value = "hover:bg-amber-600";
        icon.value = PrinterIcon;
        break;

    case "FULLY_SHIPPED":
        bgColor.value = "bg-amber-500";
        hoverBgColor.value = "hover:bg-amber-600";
        icon.value = TruckIcon;
        break;

    case "PARTIALLY_SHIPPED":
        bgColor.value = "bg-red-500";
        hoverBgColor.value = "hover:bg-red-600";
        icon.value = ExclamationCircleIcon;
        break;

    default:
        bgColor.value = "bg-amber-500";
        hoverBgColor.value = "hover:bg-amber-600";
        icon.value = ClockIcon;
}

function openModal() {
    open.value = true;

    nextTick(() => refs.status.value.focus());
}

onMounted(() => {
    if (open.value) {
        openModal();
    }
});
</script>
